import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { creditDebitTrackerApi } from '../api/creditDebitTrackerApi';
import { 
  CreditDebitTracker, 
  CreditDebitTrackerFilterParams, 
  CreditDebitTrackerFormData,
  AccountType,
  AccountBalance
} from '../types/credit-debit-tracker.types';

export const useCreditDebitTrackers = (params?: CreditDebitTrackerFilterParams) => {
  const {
    data: creditDebitTrackers = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['creditDebitTrackers', params],
    queryFn: () => creditDebitTrackerApi.getCreditDebitTrackers(params || {}),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    creditDebitTrackers,
    isLoading,
    error,
    refetch
  };
};

export const useCreditDebitTracker = (id: string) => {
  const {
    data: creditDebitTracker,
    isLoading,
    error
  } = useQuery({
    queryKey: ['creditDebitTracker', id],
    queryFn: () => creditDebitTrackerApi.getCreditDebitTrackerById(id),
    enabled: !!id,
  });

  return {
    creditDebitTracker,
    isLoading,
    error
  };
};

export const useCreateCreditDebitTracker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditDebitTrackerFormData) => creditDebitTrackerApi.createCreditDebitTracker(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditDebitTrackers'] });
    },
  });
};

export const useUpdateCreditDebitTracker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreditDebitTrackerFormData> }) =>
      creditDebitTrackerApi.updateCreditDebitTracker(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creditDebitTrackers'] });
      queryClient.invalidateQueries({ queryKey: ['creditDebitTracker', variables.id] });
    },
  });
};

export const useDeleteCreditDebitTracker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => creditDebitTrackerApi.deleteCreditDebitTracker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditDebitTrackers'] });
    },
  });
};

export const useUpdateCreditDebitTrackerStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      creditDebitTrackerApi.updateCreditDebitTrackerStatus(id, isActive),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creditDebitTrackers'] });
      queryClient.invalidateQueries({ queryKey: ['creditDebitTracker', variables.id] });
    },
  });
};

export const useAccountBalance = (chartOfAccountId: string, endDate?: string) => {
  const {
    data: accountBalance,
    isLoading,
    error
  } = useQuery({
    queryKey: ['accountBalance', chartOfAccountId, endDate],
    queryFn: () => creditDebitTrackerApi.getAccountBalance(chartOfAccountId, endDate),
    enabled: !!chartOfAccountId,
  });

  return {
    accountBalance,
    isLoading,
    error
  };
};

export const useSearchCreditDebitTrackers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: searchResults = [],
    isLoading: isSearching,
    error: searchError
  } = useQuery({
    queryKey: ['searchCreditDebitTrackers', debouncedSearchTerm],
    queryFn: () => creditDebitTrackerApi.searchCreditDebitTrackers(debouncedSearchTerm),
    enabled: debouncedSearchTerm.length > 0,
  });

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching,
    searchError
  };
};

export const useCreditDebitTrackersByDateRange = (
  startDate: string,
  endDate: string,
  filters?: {
    type?: AccountType;
    chartOfAccountId?: string;
    locationId?: string;
  }
) => {
  const {
    data: transactions = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['creditDebitTrackersByDateRange', startDate, endDate, filters],
    queryFn: () => creditDebitTrackerApi.getCreditDebitTrackersByDateRange(startDate, endDate, filters),
    enabled: !!startDate && !!endDate,
  });

  return {
    transactions,
    isLoading,
    error
  };
};
